import React from 'react';

/**
 * The one filter row: a small grey header, then pills. Same look on every page, taken
 * from Randevularım. Blue is a page's main filter, pink its second row, dark a sort.
 * (Counts go in the label as text, "Matematik (3)"; no icons or badges inside a pill.)
 *
 * `stretch` spreads the pills evenly across the row instead of letting them wrap, for
 * the 2 to 3 way choices inside a form or modal.
 */
export interface PillOption<T extends string> {
  value: T;
  label: React.ReactNode;
}

type Tone = 'blue' | 'pink' | 'dark';

// Full class strings so Tailwind keeps them.
const ACTIVE: Record<Tone, string> = {
  blue: 'bg-[#3973bf] text-white border-[#3973bf]',
  pink: 'bg-[#e5599b] text-white border-[#e5599b]',
  dark: 'bg-[#2b2b2b] text-white border-[#2b2b2b]',
};
const INACTIVE = 'bg-white text-[#2b2b2b] border-gray-200 hover:bg-gray-50';

interface Props<T extends string> {
  /** Header before the pills, written without a colon. Omit when the pills explain themselves. */
  label?: string;
  options: ReadonlyArray<PillOption<T>>;
  value: T;
  onChange: (v: T) => void;
  tone?: Tone;
  stretch?: boolean;
  className?: string;
}

function FilterPills<T extends string>({ label, options, value, onChange, tone = 'blue', stretch = false, className = '' }: Props<T>) {
  return (
    <div role="group" aria-label={label} className={`flex gap-2 items-center ${stretch ? 'w-full' : 'flex-wrap'} ${className}`}>
      {label && <span className="text-[12px] font-semibold text-gray-500 mr-1 min-w-[84px] whitespace-nowrap">{label}</span>}
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={`text-[12px] lg:text-[13px] font-semibold px-4 py-1.5 rounded-full border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3973bf] ${
              stretch ? 'flex-1' : ''
            } ${active ? ACTIVE[tone] : INACTIVE}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default FilterPills;
